import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { UtensilsCrossed, Upload, FileText, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

async function televerser(fichier) {
  const form = new FormData();
  form.append("file", fichier);
  const r = await axios.post(`${API}/upload`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const d = r.data || {};
  if (d.id) return d.id;
  const m = String(d.url || "").match(/files\/([^/?]+)/);
  if (m) return m[1];
  throw new Error("Identifiant du fichier introuvable");
}

// Photo de fiche : passe par le controle des images (type reel, taille,
// redimensionnement), contrairement au menu qui peut etre un PDF.
async function televerserPhoto(fichier) {
  const form = new FormData();
  form.append("fichier", fichier);
  const r = await axios.post(`${API}/fiches-camions/images`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  if (r.data?.file_id) return r.data.file_id;
  throw new Error("Identifiant du fichier introuvable");
}

const dateFr = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString("fr-FR", {
    day: "numeric", month: "long", year: "numeric" });
};

export default function MesMenusPage() {
  const [demandes, setDemandes] = useState([]);
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState("");
  const [manque, setManque] = useState([]);

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    try {
      const r = await axios.get(`${API}/affaires/mes/demandes`);
      setDemandes(r.data.demandes || []);
      setMotif(r.data.motif || "");
      setManque(r.data.manque || []);
    } catch {
      toast.error("Chargement impossible");
    }
  };

  const deposer = async (affaireId, fichier) => {
    if (!fichier) return;
    if (fichier.size > 10 * 1024 * 1024)
      return toast.error("Fichier trop lourd : 10 Mo maximum.");
    setEnvoi(affaireId);
    try {
      const fileId = await televerser(fichier);
      await axios.put(`${API}/affaires/${affaireId}/menu`, {
        file_id: fileId, nom_fichier: fichier.name,
      });
      toast.success("Menu déposé");
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dépôt impossible");
    } finally {
      setEnvoi("");
    }
  };

  const deposerPhoto = async (quoi, fichier) => {
    if (!fichier) return;
    if (fichier.size > 10 * 1024 * 1024)
      return toast.error("Photo trop lourde : 10 Mo maximum.");
    setEnvoi("photo");
    try {
      const fileId = await televerserPhoto(fichier);
      await axios.post(`${API}/affaires/mes/photo`, { quoi, file_id: fileId });
      toast.success("Photo enregistrée");
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally {
      setEnvoi("");
    }
  };

  if (motif)
    return (
      <div className="p-6 text-muted-foreground">
        Ce compte n'est rattaché à aucun food truck.
      </div>
    );

  const aRendre = demandes.filter((d) => !d.menu_depose).length;

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <UtensilsCrossed className="h-6 w-6" /> Mes demandes de menu
        </h1>
        <p className="text-sm text-muted-foreground">
          {aRendre
            ? `${aRendre} menu${aRendre > 1 ? "x" : ""} à déposer. Une page A4 par événement, telle qu'elle apparaîtra dans le dossier envoyé au client.`
            : "Rien à déposer pour le moment."}
        </p>
      </div>

      {!!manque.length && !!demandes.length && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-5 text-sm space-y-3">
            <p className="text-amber-900">
              <b>Il manque aussi sur votre fiche :</b> {manque.join(" · ")}.
              Sans cela, votre page du dossier restera incomplète.
            </p>
            <div className="flex gap-2 flex-wrap items-center">
              {manque.some((m) => m.includes("camion")) && (
                <label>
                  <span className="inline-flex items-center gap-1 border rounded-md px-3 py-1.5 text-xs cursor-pointer hover:bg-white">
                    <Upload className="h-3.5 w-3.5" /> Photo du camion
                  </span>
                  <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp"
                         onChange={(e) =>
                           deposerPhoto("camion", e.target.files?.[0])} />
                </label>
              )}
              {manque.some((m) => m.includes("plat")) && (
                <label>
                  <span className="inline-flex items-center gap-1 border rounded-md px-3 py-1.5 text-xs cursor-pointer hover:bg-white">
                    <Upload className="h-3.5 w-3.5" /> Photo d'un plat
                  </span>
                  <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp"
                         onChange={(e) =>
                           deposerPhoto("plat", e.target.files?.[0])} />
                </label>
              )}
              <a href="/ma-fiche-camion"
                 className="text-xs underline text-amber-900">
                Compléter toute ma fiche
              </a>
              {envoi === "photo" && (
                <span className="text-xs text-amber-800">Envoi en cours…</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {demandes.map((d) => (
        <Card key={d.affaire_id}>
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="font-medium">{d.client}</p>
                <p className="text-sm text-muted-foreground">
                  {[d.lieu, dateFr(d.date_evenement),
                    d.convives ? `${d.convives} convives` : ""]
                    .filter(Boolean).join(" · ")}
                </p>
              </div>
              {d.menu_depose ? (
                <span className="text-xs bg-green-100 text-green-900 rounded-md px-3 py-1 flex items-center gap-1">
                  <Check className="h-3.5 w-3.5" /> Menu déposé
                </span>
              ) : (
                <span className="text-xs bg-amber-100 text-amber-900 rounded-md px-3 py-1">
                  À rendre
                </span>
              )}
            </div>

            {d.menu_depose ? (
              <div className="flex items-center gap-3">
                <a href={`${API}/files/${d.menu_file_id}`} target="_blank"
                   rel="noreferrer"
                   className="h-16 w-12 border rounded flex items-center justify-center bg-muted/30">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                </a>
                <div className="flex-1 text-sm">
                  <p>Déposé le {dateFr(d.depose_le)}</p>
                  <a href={`${API}/files/${d.menu_file_id}`} target="_blank"
                     rel="noreferrer"
                     className="text-xs underline text-muted-foreground">
                    Voir le menu
                  </a>
                </div>
                <label>
                  <Button variant="outline" size="sm" asChild>
                    <span>Remplacer</span>
                  </Button>
                  <input type="file" className="hidden"
                         accept="image/*,application/pdf"
                         onChange={(e) =>
                           deposer(d.affaire_id, e.target.files?.[0])} />
                </label>
              </div>
            ) : (
              <label className="block border border-dashed rounded-md p-6 text-center cursor-pointer hover:bg-muted/20">
                <Upload className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                <p className="text-sm">
                  {envoi === d.affaire_id ? "Envoi…" : "Déposer mon menu"}
                </p>
                <p className="text-xs text-muted-foreground">
                  Une page A4, image ou PDF, conçue pour cet événement
                </p>
                <input type="file" className="hidden"
                       accept="image/*,application/pdf"
                       onChange={(e) =>
                         deposer(d.affaire_id, e.target.files?.[0])} />
              </label>
            )}
          </CardContent>
        </Card>
      ))}

      {!demandes.length && (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Aucune demande en cours. Vous serez sollicité ici dès que votre
            camion sera retenu sur un appel d'offres.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
